import React, {
    FocusEvent,
    forwardRef,
    MouseEvent,
    PropsWithChildren,
    ReactNode,
    Ref,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react"
import {
    Checkbox,
    CheckboxProps,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    IconButton,
    InputAdornment,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Radio,
    Select,
    SelectChangeEvent,
    SelectProps,
    TextField,
    TextFieldProps,
    Autocomplete,
    AutocompleteProps,
    AutocompleteValue,
    Switch,
    Typography,
    SwitchProps,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FormikValues, useField, useFormikContext } from "formik"
import { StrictOmit, StringKeyOf } from "../../../lib/lang"
import { MAX_TEXT_AREA_LENGTH, MAX_TEXT_FIELD_LENGTH } from "../../../constants/inputValidationDefaults"
import type { DatePickerRange, DateTimePickerRange } from "./datePickerRangeTypes"
import {
    getDateRangeConstraints,
    getDateTimeRangeConstraints,
    intersectMaxDate,
    intersectMinDate,
    validateDateRange,
    validateDateTimeRange,
} from "./datePickerRangeTypes"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { StandardLoadingButton, StandardLoadingButtonProps } from "../buttons/LoadingButton"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import { RichEditor } from "../editor/RichEditor"
import {
    DatePicker,
    DatePickerProps,
    DateTimePicker,
    DateTimePickerProps,
    renderTimeViewClock,
    TimePicker,
    TimePickerProps,
} from "@mui/x-date-pickers"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleCheck, faCircleXmark, faPen } from "@fortawesome/pro-light-svg-icons"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { useDebouncedEffect } from "../../hooks/useDebouncedEffect"
import { DateTime } from "luxon"

export type FormikTextFieldProps<Values extends FormikValues> = Readonly<
    StrictOmit<TextFieldProps, "name" | "value" | "error" | "onBlur" | "onChange"> & {
        name: StringKeyOf<Values>
        maxLength?: number
    }
>

const useStyles = makeStyles()((_theme) => ({
    textInput: {
        // minHeight: "4rem",
    },
    switch: {
        width: "100%",
    },
    switchLabel: {
        "&.MuiFormControlLabel-root": {
            margin: 0,
            justifyContent: "space-between",
        },
    },
    customChip: {
        display: "inline-flex",
        fontSize: 12,
        lineHeight: 1,
        borderRadius: 4,
        border: "1px solid #cbd5e1",
        background: `#F8FAFC`,
        padding: `4px 15px`,
        color: "#64748B",
        transition: "0.2s linear all",
        "& .MuiFormControlLabel-label": {
            textTransform: "capitalize",
        },
        "&.MuiFormControlLabel-root": {
            margin: 0,
        },
        "&:hover": {
            border: "1px solid #86EFAC",
            color: "#15803D",
        },
    },
    customChipChecked: {
        border: "1px solid #86EFAC",
        background: `#86EFAC55`,
        color: "#15803D",
    },
    hiddenField: {
        display: "none",
    },
    formSidebarHeading: {
        fontSize: 20,
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: "-0.5px",
        color: "#64748B",
    },
    formSidebarHeadingPadding: {
        paddingBottom: "2rem",
    },
    timePickerIconButton: {
        "& .MuiSvgIcon-root": {
            fontSize: "1.3rem",
        },
    },
    noteTextInput: {
        "& .MuiOutlinedInput-root": {
            backgroundColor: tailwindPalette.amber[50],
            "& fieldset": {
                borderColor: tailwindPalette.amber[300],
            },
            "&.Mui-disabled": {
                backgroundColor: tailwindPalette.amber[50],
                "& fieldset": {
                    borderColor: tailwindPalette.amber[300],
                },
            },
            "&:hover fieldset": {
                borderColor: tailwindPalette.amber[300],
            },
        },
    },
    icon: {
        color: tailwindPalette.amber[700],
        fontSize: "1rem",
    },
    multilineAdornment: {
        position: "absolute",
        top: ".8rem",
        right: 0,
    },
}))

export function FormikTextField<Values extends FormikValues>({
    name,
    helperText,
    maxLength = MAX_TEXT_FIELD_LENGTH,
    inputProps,
    type,
    ...rest
}: FormikTextFieldProps<Values>) {
    const { classes } = useStyles()
    const [field, meta] = useField<string>(name)

    const { submitCount } = useFormikContext<Values>()

    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    const mergedInputProps = useMemo(
        () => ({
            ...(type !== "number" ? { maxLength } : undefined),
            ...inputProps,
        }),
        [maxLength, inputProps, type],
    )

    return (
        <TextField
            name={name}
            value={field.value}
            error={showError}
            helperText={showError ? meta.error : helperText}
            onBlur={field.onBlur}
            onChange={field.onChange}
            className={classes.textInput}
            type={type}
            inputProps={mergedInputProps}
            {...rest}
        />
    )
}

export type FormikTextAreaProps<Values extends FormikValues> = Readonly<
    StrictOmit<TextFieldProps, "name" | "value" | "error" | "onBlur" | "onChange" | "multiline"> & {
        name: StringKeyOf<Values>
        maxLength?: number
        expandable?: boolean
    }
>

export function FormikTextArea<Values extends FormikValues>({
    name,
    helperText,
    maxLength = MAX_TEXT_AREA_LENGTH,
    expandable = false,
    rows,
    minRows,
    maxRows,
    inputProps,
    ...rest
}: FormikTextAreaProps<Values>) {
    const { classes } = useStyles()
    const [field, meta] = useField<string>(name)

    const { submitCount } = useFormikContext<Values>()

    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    const mergedInputProps = useMemo(
        () => ({
            maxLength,
            ...inputProps,
        }),
        [maxLength, inputProps],
    )

    const rowProps = expandable
        ? { minRows: minRows ?? 1, maxRows: maxRows ?? 4 }
        : { rows: rows ?? 4 }

    return (
        <TextField
            name={name}
            value={field.value}
            error={showError}
            helperText={showError ? meta.error : helperText}
            onBlur={field.onBlur}
            onChange={field.onChange}
            className={classes.textInput}
            multiline
            {...rowProps}
            inputProps={mergedInputProps}
            {...rest}
        />
    )
}

const INVALID_NUMBER_KEYS = [".", ",", "e", "E", "-", "+"]

export type FormikNumberFieldProps<Values extends FormikValues> = Readonly<
    StrictOmit<TextFieldProps, "name" | "value" | "error" | "onBlur" | "onChange" | "type"> & {
        name: StringKeyOf<Values>
        min?: number
        max?: number
        step?: number
        integerOnly?: boolean
        maxLength?: number
        // Callers that surface a cross-field error themselves can force the error state before the field is touched.
        error?: boolean
    }
>

export function FormikNumberField<Values extends FormikValues>({
    name,
    helperText,
    min,
    max,
    step,
    integerOnly,
    maxLength,
    inputProps,
    error,
    ...rest
}: FormikNumberFieldProps<Values>) {
    const { classes } = useStyles()
    const [field, meta, helpers] = useField<number | string>(name)

    const { submitCount } = useFormikContext<Values>()

    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (integerOnly && INVALID_NUMBER_KEYS.includes(e.key)) {
                e.preventDefault()
            }
            inputProps?.onKeyDown?.(e as React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>)
        },
        [integerOnly, inputProps],
    )

    const handleInput = useCallback(
        (e: React.FormEvent<HTMLInputElement>) => {
            if (integerOnly) {
                const digitsOnly = e.currentTarget.value.replace(/[^0-9]/gu, "")
                // Strip leading zeros, but keep a lone "0" typable for fields that allow min={0}
                const withoutLeadingZeros = digitsOnly.replace(/^0+(?=\d)/u, "")
                const limited = maxLength ? withoutLeadingZeros.slice(0, maxLength) : withoutLeadingZeros
                if (e.currentTarget.value !== limited) {
                    e.currentTarget.value = limited
                }
            }
        },
        [integerOnly, maxLength],
    )

    const mergedInputProps = useMemo(
        () => ({
            min,
            max,
            step: integerOnly ? (step ?? 1) : step,
            inputMode: integerOnly ? ("numeric" as const) : inputProps?.inputMode,
            ...inputProps,
            onKeyDown: integerOnly || inputProps?.onKeyDown ? handleKeyDown : undefined,
            onInput: integerOnly ? handleInput : inputProps?.onInput,
        }),
        [min, max, step, integerOnly, inputProps, handleKeyDown, handleInput],
    )

    const handleBlur = useCallback(
        (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            field.onBlur(e)
            const numValue = Number(field.value)
            if (!isNaN(numValue)) {
                if (max !== undefined && numValue > max) {
                    helpers.setValue(max)
                } else if (min !== undefined && numValue < min) {
                    helpers.setValue(min)
                }
            }
        },
        [field, helpers, min, max],
    )

    return (
        <TextField
            name={name}
            value={field.value}
            error={error ?? showError}
            helperText={showError ? meta.error : helperText}
            onBlur={handleBlur}
            onChange={field.onChange}
            className={classes.textInput}
            type="number"
            inputProps={mergedInputProps}
            {...rest}
        />
    )
}

type FormikRichTextEditorProps<Values extends FormikValues> = Readonly<{
    name: StringKeyOf<Values>
    placeholder?: string
    toolbar?: string
    helperText?: string
    onCharacterCountChange: (count: number) => void
}>

export function FormikRichTextEditor<Values extends FormikValues>({
    name,
    placeholder = "",
    helperText,
    toolbar,
    onCharacterCountChange,
    ...rest
}: FormikRichTextEditorProps<Values>) {
    const { classes } = useStyles()
    const [field, meta] = useField<string>(name)
    const handleChange = useCallback(
        (value: string) => {
            const handler = field.onChange(name)
            handler(value)
        },
        [field, name],
    )

    return (
        <FormControl error={Boolean(meta.error)} fullWidth>
            <RichEditor
                placeholder={placeholder}
                value={field.value}
                onBlur={field.onBlur}
                onChange={handleChange}
                toolbar={toolbar}
                onCharacterCountChange={onCharacterCountChange}
                {...rest}
            />
            <FormHelperText>{meta.error || helperText}</FormHelperText>
        </FormControl>
    )
}

type FormikAutoCompleteProps<
    Values extends FormikValues,
    TOptions,
    Multiple extends boolean = false,
    FreeSolo extends boolean = false,
> = Readonly<
    StrictOmit<AutocompleteProps<TOptions, Multiple, false, FreeSolo>, "onChange" | "value"> & {
        name: StringKeyOf<Values> // make name required and constrain it to the values managed by Formik,
        helperText?: string
        label: string
        InputProps?: TextFieldProps["InputProps"]
    }
>

export function FormikAutoComplete<
    Values extends FormikValues,
    TOption,
    Multiple extends boolean = false,
    FreeSolo extends boolean = false,
>({
    name,
    label,
    helperText,
    InputProps,
    ...rest
}: FormikAutoCompleteProps<Values, TOption, Multiple, FreeSolo>) {
    const { classes } = useStyles()
    const [field, meta, helpers] = useField(name)

    const handleChange = useCallback(
        (_event: SyntheticEvent<Element, Event>, value: AutocompleteValue<TOption, Multiple, false, FreeSolo>) => {
            helpers.setValue(value)
        },
        [field, name],
    )

    return (
        <Autocomplete
            value={field.value}
            onChange={handleChange}
            {...rest}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    name={name}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={(meta.touched && meta.error) || helperText}
                    InputProps={{
                        ...params.InputProps,
                        ...InputProps,
                    }}
                />
            )}
        />
    )
}

type FormikMultipleAutoCompleteProps<Values extends FormikValues, TOption, TValue> = Readonly<
    StrictOmit<
        AutocompleteProps<TOption, true, false, false>,
        "onChange" | "value" | "renderInput" | "isOptionEqualToValue"
    > & {
        name: StringKeyOf<Values> // make name required and constrain it to the values managed by Formik,
        label: string
        helperText?: string
        isOptionEqualToValue: (option: TOption, value: TValue) => boolean
        areOptionsEqual: (x: TOption, y: TOption) => boolean
        getOptionValue: (option: TOption) => TValue
    }
>

export function FormikMultipleAutoComplete<Values extends FormikValues, TOption, TValue>(
    props: FormikMultipleAutoCompleteProps<Values, TOption, TValue>,
) {
    const {
        name,
        label,
        helperText,
        multiple,
        options,
        isOptionEqualToValue,
        areOptionsEqual,
        getOptionValue,
        ...rest
    } = props

    const [field, meta, helpers] = useField<TValue[]>({ name, multiple: true })

    const optionValues = useMemo(() => {
        const result: TOption[] = field.value
            ? field.value.flatMap((value) => {
                  const foundOption = options.find((option) => isOptionEqualToValue(option, value))
                  if (foundOption === undefined) return []
                  return [foundOption]
              })
            : []
        return result
    }, [field.value, options])

    const handleChange = useCallback(
        (_event: SyntheticEvent<Element, Event>, options: AutocompleteValue<TOption, true, false, false>) => {
            const values = options.map((option) => getOptionValue(option))
            helpers.setValue(values)
        },
        [field, name],
    )

    return (
        <Autocomplete
            multiple
            options={options}
            value={optionValues}
            isOptionEqualToValue={areOptionsEqual}
            onBlur={field.onBlur}
            onChange={handleChange}
            {...rest}
            renderInput={(params) => (
                <TextField
                    {...params}
                    name={name}
                    label={label}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={(meta.touched && meta.error) || helperText}
                />
            )}
        />
    )
}

type FormikDropdownProps<Values extends FormikValues> = Readonly<
    StrictOmit<SelectProps, "error" | "value" | "name" | "children"> & {
        name: StringKeyOf<Values> // make name required and constrain it to the values managed by Formik
        children: ReactNode // make children required
        fullWidth?: boolean
    }
>

export function FormikDropdown<Values extends FormikValues>({
    name,
    label,
    fullWidth,
    children,
    onBlur,
    onChange,
    ...rest
}: FormikDropdownProps<Values>) {
    const { classes } = useStyles()
    const [field, meta] = useField<string>(name)

    const handleBlur = useCallback(
        (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            field.onBlur(e)
            onBlur?.(e)
        },
        [onBlur],
    )

    const handleChange = useCallback(
        (e: SelectChangeEvent<unknown>, child: ReactNode) => {
            field.onChange(e)
            onChange?.(e, child)
        },
        [onChange],
    )

    return (
        <FormControl fullWidth={fullWidth} error={meta.touched && Boolean(meta.error)}>
            <InputLabel id={name + "_label"} size={rest.size === "small" ? "small" : "normal"}>
                {label}
            </InputLabel>
            <Select
                labelId={name + "_label"}
                id={name + "_label"}
                name={name}
                onBlur={handleBlur}
                onChange={handleChange}
                value={field.value}
                label={label}
                {...rest}
            >
                {children}
            </Select>
            {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
        </FormControl>
    )
}

export function FormikPasswordField<Values extends FormikValues>(props: FormikTextFieldProps<Values>) {
    const [showPassword, setShowPassword] = useState(false)

    const toggleShowPassword = useCallback(() => setShowPassword((prev) => !prev), [setShowPassword])

    return (
        <FormikTextField
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={toggleShowPassword}>
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            {...props}
        />
    )
}

export function FormikPinField<Values extends FormikValues>(props: FormikTextFieldProps<Values>) {
    const [showPin, setShowPin] = useState(false)

    const toggleShowPin = useCallback(() => setShowPin((prev) => !prev), [setShowPin])

    return (
        <FormikTextField
            type={showPin ? "text" : "password"}
            autoComplete="off"
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={toggleShowPin}>{showPin ? <Visibility /> : <VisibilityOff />}</IconButton>
                    </InputAdornment>
                ),
            }}
            {...props}
        />
    )
}

export type FormikCheckboxProps<Values extends FormikValues> = Readonly<
    PropsWithChildren<
        StrictOmit<CheckboxProps, "name" | "value" | "checked" | "onChange"> & {
            name: StringKeyOf<Values>
            value?: string | number | readonly string[] // MUI types value prop as unknown but Formik only supports these types
            helperText?: string
            onLabelClick?: (e: MouseEvent<HTMLLabelElement>) => void
        }
    >
>

export function FormikCheckbox<Values extends FormikValues>({
    name,
    value,
    helperText,
    children,
    onLabelClick,
    ...rest
}: FormikCheckboxProps<Values>) {
    const [field, meta] = useField({ type: "checkbox", name, value })

    return (
        <FormControl error={Boolean(meta.error)}>
            <FormControlLabel
                label={children}
                control={
                    <Checkbox name={name} value={value} checked={field.checked} onChange={field.onChange} {...rest} />
                }
                onClick={onLabelClick}
            />
            <FormHelperText>{meta.error || helperText}</FormHelperText>
        </FormControl>
    )
}

type FormikRadioListItemProps<Values extends FormikValues> = Readonly<
    PropsWithChildren<{
        name: StringKeyOf<Values>
        value: string | number | undefined
    }>
>

export function FormikRadioListItem<Values extends FormikValues>({
    name,
    value,
    children,
}: FormikRadioListItemProps<Values>) {
    const [field, _, helpers] = useField({ type: "radio", name, value })

    return (
        <>
            <ListItem button onClick={() => helpers.setValue(value)}>
                <ListItemText primary={children} />
                <ListItemSecondaryAction>
                    <Radio name={name} value={value} checked={field.checked} onChange={() => helpers.setValue(value)} />
                </ListItemSecondaryAction>
            </ListItem>
            <Divider />
        </>
    )
}

type FormikSubmitButtonProps = StrictOmit<StandardLoadingButtonProps, "disabled" | "loading"> & {
    parentDisabled?: boolean
}

export const FormikSubmitButton = forwardRef(
    ({ children, parentDisabled, ...rest }: FormikSubmitButtonProps, ref: Ref<HTMLButtonElement>) => {
        const { isValid, isSubmitting, submitCount } = useFormikContext()

        const disabled = parentDisabled || (!isValid && submitCount > 0)

        return (
            <StandardLoadingButton ref={ref} type="submit" disabled={disabled} loading={isSubmitting} {...rest}>
                {children}
            </StandardLoadingButton>
        )
    },
)

type FormikSwitchProps<Values extends FormikValues> = Readonly<
    PropsWithChildren<
        StrictOmit<SwitchProps, "name" | "value" | "checked" | "onChange"> & {
            name: StringKeyOf<Values>
            value?: string | number | readonly string[] // MUI types value prop as unknown but Formik only supports these types
            onLabelClick?: (e: MouseEvent<HTMLLabelElement>) => void
            formSidebarHeading?: boolean
            labelPlacement?: "end" | "start" | "top" | "bottom"
        }
    >
>

export function FormikSwitch<Values extends FormikValues>({
    name,
    value,
    children,
    onLabelClick,
    formSidebarHeading,
    labelPlacement = "start",
    ...rest
}: FormikSwitchProps<Values>) {
    const [field, meta] = useField({ type: "checkbox", name, value })
    const { classes, cx } = useStyles()
    return (
        <FormControl
            className={cx(classes.switch, {
                [classes.formSidebarHeadingPadding]: formSidebarHeading,
            })}
            error={Boolean(meta.error)}
        >
            <FormControlLabel
                className={classes.switchLabel}
                labelPlacement={labelPlacement}
                label={
                    formSidebarHeading ? (
                        <Typography className={classes.formSidebarHeading} variant="h3">
                            {children}
                        </Typography>
                    ) : (
                        children
                    )
                }
                control={
                    <Switch name={name} value={value} checked={field.checked} onChange={field.onChange} {...rest} />
                }
                onClick={onLabelClick}
            />
            <FormHelperText>{meta.error}</FormHelperText>
        </FormControl>
    )
}

export function FormikChip<Values extends FormikValues>({
    name,
    value,
    children,
    onLabelClick,
    ...rest
}: FormikCheckboxProps<Values>) {
    const [field, meta, helpers] = useField({ type: "checkbox", name, value })
    const { classes, cx } = useStyles()

    return (
        <FormControl className={classes.switch} error={Boolean(meta.error)}>
            <FormControlLabel
                className={cx(classes.customChip, {
                    [classes.customChipChecked]: field.checked,
                })}
                label={children}
                labelPlacement="start"
                control={
                    <Checkbox
                        className={classes.hiddenField}
                        name={name}
                        value={value}
                        onChange={field.onChange}
                        {...rest}
                    />
                }
                onClick={onLabelClick}
            />
            <FormHelperText>{meta.error}</FormHelperText>
        </FormControl>
    )
}

type FormikTimePickerProps<Values extends FormikValues, TDate> = Readonly<
    StrictOmit<TimePickerProps<TDate>, "name" | "value" | "onChange" | "slotProps"> & {
        name: StringKeyOf<Values>
        value?: string
        forceOpen?: boolean
        showMobilePicker?: boolean
        /**
         * Called after the picked time has been written to the field.
         * Use it for cross-field work (adjusting a sibling field, rebuilding a derived value).
         */
        onValueChange?: (value: TDate | null) => void
        /**
         * Merged into the component's own slot props instead of replacing them, so a caller can
         * restyle the field without dropping the blur handler that commits the value.
         * Only the object form is supported; MUI's callback form is not.
         */
        slotProps?: StrictOmit<NonNullable<TimePickerProps<TDate>["slotProps"]>, "textField"> & {
            textField?: StrictOmit<TextFieldProps, "onBlur">
        }
    }
>

export function FormikTimePicker<Values extends FormikValues, TDate>({
    name,
    value,
    label,
    disabled = false,
    forceOpen = false,
    showMobilePicker = false,
    onValueChange,
    slotProps,
    ...rest
}: FormikTimePickerProps<Values, TDate>) {
    const [field, meta, helpers] = useField<TDate | null>({ type: "text", name, value })
    const { classes } = useStyles()
    const [open, setOpen] = useState(false)
    const step = 5

    const [localValue, setLocalValue] = useState<TDate | null>(field.value ?? null)

    useEffect(() => {
        if (forceOpen && !open) {
            setOpen(true)
        }
    }, [forceOpen])

    useEffect(() => {
        if (field.value !== localValue) {
            setLocalValue(field.value ?? null)
        }
    }, [field.value])

    const roundTime = (val: TDate): TDate => {
        if (val && typeof (val as any).toFormat === "function" && DateTime.isDateTime(val as any)) {
            const dt = val as unknown as DateTime
            return dt.set({
                minute: Math.round(dt.minute / step) * step,
                second: 0,
                millisecond: 0,
            }) as any as TDate
        }
        return val
    }

    const commit = (val: TDate | null) => {
        if (!val) return
        const next = roundTime(val)
        helpers.setValue(next)
        onValueChange?.(next)
    }

    const { textField: callerTextField, ...callerSlots } = slotProps ?? {}

    return (
        <FormControl className={classes.switch} error={Boolean(meta.error)}>
            <TimePicker<TDate>
                label={label}
                value={localValue}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                disabled={disabled}
                // MUI reports null for every half-typed value, so the form is only written on accept/blur.
                onChange={(val) => {
                    setLocalValue(val)
                }}
                onAccept={(val) => commit(val)}
                minutesStep={step}
                viewRenderers={
                    showMobilePicker ? { hours: renderTimeViewClock, minutes: renderTimeViewClock } : undefined
                }
                slotProps={{
                    openPickerButton: { className: classes.timePickerIconButton },
                    popper: {
                        sx: {
                            "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar": {
                                width: "1px",
                            },
                        },
                    },
                    ...callerSlots,
                    textField: {
                        variant: "outlined",
                        size: "small",
                        fullWidth: true,
                        // An empty picker would otherwise drop its label onto the clock icon.
                        InputLabelProps: { shrink: true },
                        ...callerTextField,
                        onBlur: () => commit(localValue),
                    },
                }}
                {...rest}
            />
            {meta.error && <FormHelperText>{meta.error}</FormHelperText>}
        </FormControl>
    )
}

export function FormikNoteInput<Values extends FormikValues>({
    name,
    helperText,
    maxLength = MAX_TEXT_AREA_LENGTH,
    inputProps,
    ...rest
}: FormikTextFieldProps<Values>) {
    const { classes } = useStyles()
    const [field, meta, helpers] = useField(name)
    const [isEditable, setEditable] = useState(false)

    const mergedInputProps = useMemo(() => ({ maxLength, ...inputProps }), [maxLength, inputProps])

    const handleEditClick = () => setEditable(true)
    const handleSaveClick = () => setEditable(false)
    const handleCancelClick = () => {
        setEditable(false)
        helpers.setValue("")
    }

    const endAdornment = isEditable ? (
        <InputAdornment position="end" className={isEditable ? classes.multilineAdornment : ""}>
            <IconButton size="small" onClick={handleSaveClick}>
                <FontAwesomeIcon icon={faCircleCheck} className={classes.icon} />
            </IconButton>
            <IconButton size="small" onClick={handleCancelClick}>
                <FontAwesomeIcon icon={faCircleXmark} className={classes.icon} />
            </IconButton>
        </InputAdornment>
    ) : (
        <InputAdornment position="end">
            <IconButton size="small" className={classes.icon} onClick={handleEditClick}>
                <FontAwesomeIcon icon={faPen} />
            </IconButton>
        </InputAdornment>
    )

    const formikFieldProps = {
        ...field,
        disabled: !isEditable,
        error: meta.touched && Boolean(meta.error),
        helperText: (meta.touched && meta.error) || helperText,
        onBlur: field.onBlur,
        onChange: field.onChange,
    }

    return (
        <TextField
            className={classes.noteTextInput}
            multiline={isEditable}
            rows={isEditable ? 3 : 1}
            InputProps={{
                endAdornment,
            }}
            inputProps={mergedInputProps}
            {...formikFieldProps}
            {...rest}
        />
    )
}

type FormikDatePickerProps<Values extends Record<string, unknown>, TDate> = Readonly<{
    name: keyof Values
    label?: string
    disabled?: boolean
    range: DatePickerRange
}> &
    Omit<DatePickerProps<TDate>, "value" | "onChange" | "renderInput">

export function FormikDatePicker<Values extends Record<string, unknown>, TDate>({
    name,
    label,
    disabled = false,
    range,
    minDate: explicitMinDate,
    maxDate: explicitMaxDate,
    ...rest
}: FormikDatePickerProps<Values, TDate>) {
    const rangeConstraints = useMemo(() => getDateRangeConstraints(range), [range])

    const effectiveMinDate = intersectMinDate(rangeConstraints.minDate, explicitMinDate as DateTime | undefined) as
        | TDate
        | undefined
    const effectiveMaxDate = intersectMaxDate(rangeConstraints.maxDate, explicitMaxDate as DateTime | undefined) as
        | TDate
        | undefined

    const { l10n } = useAppLocalization()

    const [field, meta, helpers] = useField<TDate | null>({
        name: name as string,
        validate: (value: TDate | null) => {
            if (value && DateTime.isDateTime(value)) {
                const errorKey = validateDateRange(value, range)
                return errorKey ? l10n.getString(errorKey) : undefined
            }
            return undefined
        },
    })
    const { setValue, setTouched } = helpers
    const { submitCount } = useFormikContext<Values>()
    const { classes } = useStyles()

    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    const handleChange = (newValue: TDate | null) => {
        setTouched(true, false)
        setValue(newValue)
    }

    return (
        <FormControl className={classes.switch} error={showError}>
            <DatePicker<TDate>
                value={field.value}
                onChange={handleChange}
                label={label}
                disabled={disabled}
                minDate={effectiveMinDate}
                maxDate={effectiveMaxDate}
                {...rest}
            />
            {showError && <FormHelperText>{meta.error}</FormHelperText>}
        </FormControl>
    )
}

export function FormikDatePickerControlled<Values extends Record<string, unknown>, TDate>(
    props: FormikDatePickerProps<Values, TDate>,
) {
    const { name, label, disabled = false, range, minDate: explicitMinDate, maxDate: explicitMaxDate, ...rest } = props

    const rangeConstraints = useMemo(() => getDateRangeConstraints(range), [range])

    const effectiveMinDate = intersectMinDate(rangeConstraints.minDate, explicitMinDate as DateTime | undefined) as
        | TDate
        | undefined
    const effectiveMaxDate = intersectMaxDate(rangeConstraints.maxDate, explicitMaxDate as DateTime | undefined) as
        | TDate
        | undefined

    const { l10n } = useAppLocalization()

    const [field, meta, helpers] = useField<TDate | null>({
        name: name as string,
        validate: (value: TDate | null) => {
            if (value && DateTime.isDateTime(value)) {
                const errorKey = validateDateRange(value, range)
                return errorKey ? l10n.getString(errorKey) : undefined
            }
            return undefined
        },
    })
    const { setValue, setTouched } = helpers
    const { submitCount } = useFormikContext<Values>()
    const { classes } = useStyles()
    const [open, setOpen] = useState(false)

    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    const handleChange = (newValue: TDate | null) => {
        setTouched(true, false)
        setValue(newValue)
        setOpen(false)
    }

    useDebouncedEffect(() => {
        if (!field.value) {
            setOpen(true)
        }
    }, [field.value])

    return (
        <FormControl className={classes.switch} error={showError}>
            <DatePicker<TDate>
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={field.value}
                onChange={handleChange}
                label={label}
                disabled={disabled}
                minDate={effectiveMinDate}
                maxDate={effectiveMaxDate}
                {...rest}
            />
            {showError && <FormHelperText>{meta.error}</FormHelperText>}
        </FormControl>
    )
}

type FormikDateTimePickerProps<Values extends Record<string, unknown>, TDate> = Readonly<{
    name: keyof Values
    label?: string
    disabled?: boolean
    range: DateTimePickerRange
}> &
    Omit<DateTimePickerProps<TDate>, "value" | "onChange" | "renderInput">

export function FormikDateTimePicker<Values extends Record<string, unknown>, TDate>({
    name,
    label,
    disabled = false,
    range,
    minDateTime: explicitMinDateTime,
    maxDateTime: explicitMaxDateTime,
    ...rest
}: FormikDateTimePickerProps<Values, TDate>) {
    const rangeConstraints = useMemo(() => getDateTimeRangeConstraints(range), [range])

    const effectiveMinDateTime = intersectMinDate(
        rangeConstraints.minDateTime,
        explicitMinDateTime as DateTime | undefined,
    ) as TDate | undefined
    const effectiveMaxDateTime = intersectMaxDate(
        rangeConstraints.maxDateTime,
        explicitMaxDateTime as DateTime | undefined,
    ) as TDate | undefined

    const { l10n } = useAppLocalization()

    const [field, meta, helpers] = useField<TDate | null>({
        name: name as string,
        validate: (value: TDate | null) => {
            if (value && DateTime.isDateTime(value)) {
                const errorKey = validateDateTimeRange(value, range)
                return errorKey ? l10n.getString(errorKey) : undefined
            }
            return undefined
        },
    })
    const { setValue, setTouched } = helpers
    const { submitCount } = useFormikContext<Values>()
    const { classes } = useStyles()

    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    const handleChange = (newValue: TDate | null) => {
        setTouched(true, false)
        setValue(newValue)
    }

    return (
        <FormControl className={classes.switch} error={showError}>
            <DateTimePicker<TDate>
                value={field.value}
                onChange={handleChange}
                label={label}
                disabled={disabled}
                minDateTime={effectiveMinDateTime}
                maxDateTime={effectiveMaxDateTime}
                {...rest}
            />
            {showError && <FormHelperText>{meta.error}</FormHelperText>}
        </FormControl>
    )
}
